import { useMapStoreContext } from 'app/src/editor/MapStoreContext';
import Solver, { SolveResult } from 'app/src/editor/Solver';
import { InstructionType } from 'app/src/engine/Instruction';
import InstructionNode from 'app/src/game/InstructionNode';
import Modal from 'app/src/modals/Modal';
import { formatNumber } from 'app/src/utils/NumberUtils';
import useAsyncEffect from 'app/src/utils/useAsyncEffect';
import useForceUpdate from 'app/src/utils/useForceUpdate';
import useInitializer from 'app/src/utils/useInitializer';
import wait from 'app/src/utils/wait';
import * as React from 'react';

interface Props {
	onClose: () => void;
	onError: (message: string, details: string) => void;
}

const TRIALS_PER_UPDATE = 1000;

export default function SolutionsPopup({ onClose }: Props): React.ReactElement {
	const { mapStore } = useMapStoreContext();

	const solver = useInitializer(
		() => Solver.sharedInstance.solveLevel(mapStore.buildLevelDescription()),
		[],
	);
	const [doneSolving, setDoneSolving] = React.useState(false);
	const [solutions, setSolutions] = React.useState<InstructionType[][] | null>([]);
	const trialsRef = React.useRef(0);
	const forceUpdate = useForceUpdate();

	useAsyncEffect(
		async (isMounted): Promise<void> => {
			let solving = true;

			do {
				const { done, value }: IteratorResult<SolveResult, SolveResult> = solver.next();

				if (done) {
					solving = false;
					trialsRef.current = value.trials;
					setDoneSolving(true);
					setSolutions(value.solutions);
					forceUpdate();
				} else {
					// Keep looping until we get a set number up trials, then give React a
					// chance to update. If we finish before we reach that update count,
					// the state setters above will finish the loop and update React.
					if (value.trials >= trialsRef.current + TRIALS_PER_UPDATE) {
						trialsRef.current = value.trials;
						setSolutions(value.solutions);
						forceUpdate();
						await wait();
					}
				}
			} while (solving && isMounted());
		},
		[solver],
	);

	return (
		<Modal className="SolutionsPopup">
			<div className="SolutionsPopup-title">
				{doneSolving
					? `Done solving after ${formatNumber(trialsRef.current)} trials.`
					: `Solving. ${formatNumber(trialsRef.current)} trials tested…`}
			</div>
			{solutions && solutions.length !== 0 && (
				<div className="SolutionsPopup-solutionsContainer">
					{solutions.map((solution, solutionIndex) => (
						<div className="SolutionsPopup-solution" key={`solution-${solutionIndex}`}>
							{solution.map((instructionType, instructionTypeIndex) => (
								<div
									key={`instruction-${solutionIndex}-${instructionTypeIndex}`}
									className="SolutionsPopup-instructionContainer"
								>
									<InstructionNode type={instructionType} />
								</div>
							))}
						</div>
					))}
				</div>
			)}
			<div className="SolutionsPopup-controls">
				<button
					className="SolutionsPopup-control SolutionsPopup-control--ok"
					onClick={(): void => onClose()}
				>
					{doneSolving ? 'OK' : 'Cancel'}
				</button>
			</div>
		</Modal>
	);
}
